import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import type { Colleague } from '@/types'
import { Select } from '@/components/ui/Select'

/** Mirrors JobStageLog::MAX_HELPERS on the backend. */
export const MAX_HELPERS = 5

/**
 * "Worked with" picker for the complete dialog. Colleagues from the
 * operator's own section show as toggle chips; anyone from another section is
 * added through a searchable dropdown and then shows as a removable chip. The
 * server re-checks every pick, so this is a convenience, not the gate.
 */
export default function HelperPicker({
  value,
  onChange,
  disabled,
  enabled = true,
}: {
  value: number[]
  onChange: (ids: number[]) => void
  disabled?: boolean
  /** Fetch only while the dialog is open. */
  enabled?: boolean
}) {
  const colleagues = useQuery({
    queryKey: ['staff-colleagues'],
    queryFn: async () => (await api.get<{ data: Colleague[] }>('/staff/colleagues')).data.data,
    enabled,
    staleTime: 5 * 60_000,
  })

  const all = colleagues.data ?? []
  const full = value.length >= MAX_HELPERS

  const toggle = (id: number) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id))
    else if (!full) onChange([...value, id])
  }

  const own = all.filter((c) => c.same_section)
  const otherPicked = all.filter((c) => !c.same_section && value.includes(c.id))
  const otherOptions = all
    .filter((c) => !c.same_section && !value.includes(c.id))
    .map((c) => ({ value: String(c.id), label: c.department ? `${c.name} (${c.department})` : c.name }))

  if (colleagues.isLoading) return <p className="text-xs text-muted">Loading colleagues...</p>
  // Nobody else to credit, so the dialog stays as it was.
  if (all.length === 0) return null

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">
        Worked with (optional) - {value.length} of {MAX_HELPERS}
      </p>

      {(own.length > 0 || otherPicked.length > 0) && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {own.map((c) => {
            const on = value.includes(c.id)
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                disabled={disabled || (!on && full)}
                onClick={() => toggle(c.id)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-40',
                  on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
                )}
              >
                {c.name}
              </button>
            )
          })}
          {otherPicked.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(c.id)}
              aria-label={`Remove ${c.name}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary/60 bg-primary/20 px-3 py-2 text-sm font-semibold text-primary"
            >
              {c.name}
              {c.department && <span className="font-normal opacity-70">({c.department})</span>}
              <X className="size-3.5" />
            </button>
          ))}
        </div>
      )}

      {otherOptions.length > 0 && (
        <Select
          className="mt-2"
          value=""
          onChange={(id) => id && toggle(Number(id))}
          options={otherOptions}
          placeholder={full ? `${MAX_HELPERS} teammates is the most` : 'Add from another section'}
          searchable
          searchPlaceholder="Search a name"
          disabled={disabled || full}
        />
      )}
    </div>
  )
}
